import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { handleResetPassword } from '../utils/api-handlers/authentication-forms';

const resetPasswordSchema = z.object({
  password: z.string()
    .min(1, '請輸入密碼')
    .min(6, '密碼至少需要6個字元'),
  confirmPassword: z.string()
    .min(1, '請再次輸入密碼')
}).refine((data) => data.password === data.confirmPassword, {
  message: "密碼不相符",
  path: ["confirmPassword"],
});

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = location.pathname.split('/resetpkey/')[1]; // 獲取原始編碼的 token
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(resetPasswordSchema)
  });

  const onSubmit = async (data) => {
    // 確認是否有 token
    if (!token) {
      toast.error('無效的重設連結');
      return;
    } else {
    }
    try {
      const result = await handleResetPassword({ 
        token, 
        newPKey: data.password?.trim(), 
        newPKeyConfirm: data.confirmPassword?.trim() 
      });
      if (result?.success) {
        setTimeout(() => {
          navigate('/login');
        }, 2000);   
      }
    } catch (error) {
      console.error('Reset password error:', error);
    }
  };

  // 如果沒有 token，顯示錯誤訊息
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-center text-2xl font-bold text-red-600 mb-4">
            無效的重設連結
          </h2>
          <p className="text-center text-gray-600">
            請重新申請重設密碼，或聯繫系統管理員
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="mt-4 w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            重新申請
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            重設密碼
          </h2>
          {/* <p className="mt-2 text-center text-sm text-gray-600">
            請輸入新密碼
          </p> */}
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                新密碼
              </label>
              <div className='relative'>
                <input
                  id="password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  {...register('password')}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    errors.password ? 'border-red-500' : ''
                  }`}
                />
                {isPasswordVisible ? (
                  <Eye className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20} onClick={() => setIsPasswordVisible(false)} />
                ) : (
                  <EyeOff className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20}  onClick={() => setIsPasswordVisible(true)} />
                )}
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                確認新密碼
              </label>
              <div className='relative'>
                <input
                  id="confirmPassword"
                  type={isPasswordVisible ? 'text' : 'password'}
                  {...register('confirmPassword')}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                />
                {isPasswordVisible ? (
                  <Eye className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20} onClick={() => setIsPasswordVisible(false)} />
                ) : (
                  <EyeOff className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-fourth mr-1.5" size={20}  onClick={() => setIsPasswordVisible(true)} />
                )}
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
          >
            {isSubmitting ? '處理中...' : '重設密碼'}
          </button>
        </form>
        <div className='flex justify-between'>
          <Link
            to="/login"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            返回登入
          </Link>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            重新獲取驗證信
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;